import { useEffect, useRef, useState, useCallback } from 'react';
import { axios, publicAxios, deep_copy } from './axios';
import { config } from '@/config';
import i18n from 'i18next';
import { decode } from '@/base64';

export function parseQueryString(str) {
  str = str.split('#')[0];
  if (str[0] === '?') str = str.slice(1);
  let pairs = str.split('&');
  let result = {};
  for (let pair of pairs) {
    if (pair === '') continue;
    let keyValue = pair.split('=');
    result[keyValue[0]] = keyValue[1];
  }
  return result;
}

export { deep_copy };

export function isEmpty(obj) {
  if (!obj) return true;
  return Object.keys(obj).length === 0;
}

export function isObject(x) {
  return typeof x === 'object' && !Array.isArray(x) && x !== null;
}

export const cleverClone = (o) => {
  if (!o) return o;
  return JSON.parse(JSON.stringify(o, cloneReplacer), cloneReviver(o));
};

const cloneReplacer = (_key, value) => {
  if (value instanceof Object && value.serverLength !== undefined) {
    const { serverLength } = value;
    return { ...value, serverLength };
  }
  return value;
};

const cloneReviver = () => (key, value) => {
  if (
    value !== null &&
    typeof value === 'object' &&
    Object.prototype.hasOwnProperty.call(value, 'serverLength')
  ) {
    const { serverLength } = value;
    delete value.serverLength;
    Object.defineProperty(value, 'serverLength', {
      enumerable: false,
      value: serverLength,
      writable: true,
    });
    return value;
  }
  return value;
};

export class Deferred {
  constructor() {
    this._promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    this.then = this._promise.then.bind(this._promise);
    this.catch = this._promise.catch.bind(this._promise);
  }
  _resolve = () => {
    let temp = new Deferred();
    temp.resolve();
    return temp;
  };
}

export function isString(str) {
  return typeof str === 'string' || str instanceof String;
}

export function parseRole(role) {
  switch (role) {
    case 10:
      return 'role.CANDIDATE';
    case 20:
      return 'role.STUDENT';
    case 40:
      return 'role.TEAMLEAD';
    case 45:
      return 'role.CONTENT_CREATOR';
    case 60:
      return 'role.MENTOR';
    case 70:
      return 'role.TEACHER';
    case 75:
      return 'role.RECRUITER';
    case 90:
      return 'role.ADMIN';
    case 95:
      return 'role.SUPER_ADMIN';
    default:
      return 'role.UNKOWN';
  }
}

export function mapRole(role) {
  if (role >= 95) return 95;
  else if (role >= 90) return 90;
  else if (role >= 75) return 75;
  else if (role >= 70) return 70;
  else if (role >= 40) return 40;
  else if (role >= 30) return 30;
  else if (role >= 20) return 20;
  else if (role >= 10) return 10;
}

import { useMediaQuery } from 'react-responsive';

export const useMobile = () => {
  return useMediaQuery({ query: '(max-width: 767px)' });
};

export function newUid(n) {
  const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const valid_chars =
    '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

  // First character must be a letter
  let text = letters.charAt(Math.floor(Math.random() * letters.length));

  // Remaining characters can be anything from the full valid set
  for (let i = 1; i < n; i++) {
    text += valid_chars.charAt(Math.floor(Math.random() * valid_chars.length));
  }

  return text;
}

/** builds a pretty tree based lessons, directories
 *
 * returns [prettyTree, lessonLst, directoryLst]
 *
 * prettyTree: Recursive structure of course
 * lessonLst: All lessons in DFS order
 * directoryLst: All directories in DFS order
 *
 */
export function buildTree(lessons, directories) {
  directories = directories.reduce((obj, el) => {
    obj[el.path.slice(el.path.lastIndexOf('/') + 1)] = el;
    return obj;
  }, {});

  let tree = {};
  for (let lesson of lessons) {
    let paths = lesson.path.split('/');
    paths = paths.filter((x) => x);
    let currentPath = tree;
    for (let path of paths) {
      if (!currentPath[path]) currentPath[path] = {};
      currentPath = currentPath[path];
    }
    Object.assign(currentPath, lesson);
  }

  let prettyTree = Object.assign({}, { children: [] });

  let lessonLst = [];
  let directoryLst = [];

  let parse = (node, path, prettyTree) => {
    let lst = Object.entries(node);
    lst = lst.sort((a, b) => {
      a =
        typeof a[1].yindex !== 'undefined'
          ? a[1].yindex
          : directories[a[0]]?.yindex;
      b =
        typeof b[1].yindex !== 'undefined'
          ? b[1].yindex
          : directories[b[0]]?.yindex;
      return a - b;
    });
    for (let node of lst) {
      if (node[1].lessonId) {
        let lesson = node[1];
        let newPath = path + lesson.name;
        let newNode = Object.assign({ pathStr: newPath }, lesson);
        prettyTree.push(newNode);
        lessonLst.push(newNode);
      } else {
        let directory = directories[node[0]];
        let newPath = path + directory.name;
        let newNode = Object.assign({ pathStr: newPath }, directory);
        newNode.children = [];
        prettyTree.push(newNode);
        directoryLst.push(newNode);
        parse(node[1], newPath + '/', newNode.children);
      }
    }
  };

  // console.log(prettyTree, lessonLst, directoryLst);

  parse(tree, '/', prettyTree.children);
  return [prettyTree, lessonLst, directoryLst];
}

/** builds a pretty tree structured for react-sortable-tree
 *
 *     treeData: [
 *       { title: 'Chicken', children: [{ title: 'Egg' }] },
 *       { title: 'Fish', children: [{ title: 'fingerline' }] },
 *     ],
 *
 * returns [prettyTree, lessonLst, directoryLst]
 *
 * prettyTree: Recursive structure of course
 * lessonLst: All lessons in DFS order
 * directoryLst: All directories in DFS order
 *
 */
export function buildSortedTree(lessons, directories) {
  let nodes = [...lessons, ...directories];
  let tree = {};
  for (let node of nodes) {
    let paths = node.path.split('/');
    paths = paths.filter((x) => x);
    let currentPath = tree;
    for (let path of paths) {
      if (!currentPath[path]) currentPath[path] = {};
      currentPath = currentPath[path];
    }
  }

  let indexedNodes = nodes.reduce((obj, node) => {
    obj[node.path] = node;
    return obj;
  }, {});

  let prettyTree = { children: [] };
  let parse = (node, path, prettyTree) => {
    let lst = Object.keys(node);
    let nodes = lst.map((x) => {
      if (indexedNodes[path + x] === undefined) {
        indexedNodes[path + x] = {
          name: 'broken, pls remove',
          path: path + x,
          id: newUid(20),
          yindex: 0,
        };
      }
      return indexedNodes[path + x];
    });

    prettyTree.push(...nodes);
    for (let i in nodes) {
      if (!nodes[i]) nodes[i] = { id: newUid(20) };
      nodes[i].children = [];
      parse(node[lst[i]], nodes[i].path + '/', nodes[i].children);
    }
    prettyTree.sort((a, b) => a.yindex - b.yindex);

    let order = 1;
    prettyTree.forEach((node) => {
      if (node.numbered) {
        node.order = order++; // Присвоюємо порядковий номер та збільшуємо лічильник
      }
    });
  };
  parse(tree, '/', prettyTree.children);
  return prettyTree;
}

export function walk(root, fn, i = 0, parent = {}) {
  if (!root) return;
  fn(root, i, parent);
  if (root.children) {
    let j = 0;
    for (let child of root.children) {
      walk(child, fn, j, root);
      j++;
    }
  }
}

export function bfsWalk(root, fn, i = 0, parent = {}) {
  const queue = [root];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === null) continue;
    fn(current);
    if (current.children) {
      for (const child of current.children) {
        queue.push(child);
      }
    }
    if (current.extraInfo) {
      for (const child of current.extraInfo) {
        queue.push(child);
      }
    }
  }
}

export function walkObject(obj, fn, key = 'root', parent = null) {
  if (obj === null || typeof obj !== 'object') return;

  fn(obj, key, parent);

  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      walkObject(obj[i], fn, i, obj);
    }
  } else {
    for (let k of Object.keys(obj)) {
      walkObject(obj[k], fn, k, obj);
    }
  }
}

export function bfsWalkObject(obj, fn, key = 'root', parent = null) {
  if (obj === null || typeof obj !== 'object') return;

  const queue = [{ value: obj, key, parent }];
  const visited = new Set();

  while (queue.length > 0) {
    const { value, key, parent } = queue.shift();

    if (value === null || typeof value !== 'object') continue;
    if (visited.has(value)) continue;
    visited.add(value);

    const result = fn(value, key, parent);
    if (result !== undefined) return result;

    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        queue.push({ value: value[i], key: i, parent: value });
      }
    } else {
      for (const k of Object.keys(value)) {
        queue.push({ value: value[k], key: k, parent: value });
      }
    }
  }
}

export function firstOfDfs(root, fn, i = 0, parent = {}) {
  if (!root) return;
  if (fn(root, i, parent)) return root;
  if (root.children) {
    let j = 0;
    for (let child of root.children) {
      let result = firstOfDfs(child, fn, j, root);
      if (result) return result;
      j++;
    }
  }
}

export function parseJwt(token) {
  if (!token) return {};
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

function uniqueBy(arr, fn) {
  let unique = {};
  let distinct = [];
  arr.forEach(function (x) {
    let key;
    if (fn) key = fn(x);
    else key = x;
    if (!unique[key]) {
      distinct.push(x);
      unique[key] = true;
    }
  });
  return distinct;
}

export function unique(arr) {
  return uniqueBy(arr, (x) => x);
}

/** builds a pretty tree structured for react-dnd-treeview
 *
    [
      {
        "id": 1,
        "parent": 0,
        "droppable": true,
        "text": "Folder 1"
      },
      {
        "id": 2,
        "parent": 1,
        "text": "File 1-1"
      },
      {
        "id": 3,
        "parent": 1,
        "text": "File 1-2"
      },
      {
        "id": 4,
        "parent": 0,
        "droppable": true,
        "text": "Folder 2"
      },
      {
        "id": 5,
        "parent": 4,
        "droppable": true,
        "text": "Folder 2-1"
      },
      {
        "id": 6,
        "parent": 5,
        "text": "File 2-1-1"
      }
    ]
 *
 * returns [prettyTree, lessonLst, directoryLst]
 *
 * prettyTree: Recursive structure of course
 * lessonLst: All lessons in DFS order
 * directoryLst: All directories in DFS order
 *
 */
export function buildTreeFlat(lessons, directories) {
  const prettyTree = buildSortedTree(lessons, directories);

  let flatTree = [];
  prettyTree.id = 0;
  prettyTree.path = '/';

  walk(prettyTree, (node, i, parent) => {
    flatTree.push({
      id: node.lessonId || node?.assetId ? node.usesId : node.id,
      parent: parent.id,
      droppable: node.lessonId || node?.assetId ? false : true,
      text: node?.name || node?.assetName || 'Broken node',
      isOpen: true,
      data: node,
    });
  });

  return [flatTree.slice(1)];
}

export function loadRawScript(src) {
  return new Promise((resolve, reject) => {
    var tag = window.document.createElement('script');
    tag.type = 'text/javascript';
    tag.text = src;
    /*
    tag.onload = function () {
      resolve();
    };
    */
    window.document.getElementsByTagName('body')[0].appendChild(tag);
    resolve();
  });
}

export function loadStyle(src) {
  var tag = window.document.createElement('link');
  tag.rel = 'stylesheet';
  tag.type = 'text/css';
  tag.href = src;
  window.document.getElementsByTagName('body')[0].appendChild(tag);
}

const s3Alphabet =
  "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ/!-_.*'()";
//see https://github.com/GeorgePhillips/node-s3-url-encode/blob/master/index.js
export function encodeS3URI(filename) {
  let safeName = '';
  for (let i = 0; i < filename.length; i++) {
    if (s3Alphabet.includes(filename[i])) safeName += filename[i];
  }
  return safeName;
}

export function encodeRandomS3URI(filename) {
  let splitted = encodeS3URI(filename).split('.');
  if (splitted.length > 1) splitted[splitted.length - 2] += '-' + newUid(20);
  return splitted.join('.');
}

export function getSafe(fn, defaultValue) {
  try {
    let result = fn();
    return result === null || result === undefined ? defaultValue : result;
  } catch (e) {
    return defaultValue;
  }
}

export function last(arr) {
  return arr[arr.length - 1];
}

export const round = (n, d) => Number(Math.round(n + 'e' + d) + 'e-' + d);
export const round2 = (n) => round(n, 2);

export function shuffle(arr) {
  let random_array = arr.map((x) => [x, Math.random()]);
  random_array.sort((a, b) => a[1] - b[1]);
  return random_array.map((x) => x[0]);
}

export function kendallsTau(arr) {
  if (!Array.isArray(arr)) return 0;

  const n = arr.length;
  // 0 or 1 items are always in the correct order.
  if (n <= 1) return 1;

  let correctPairs = 0;
  for (let i = 0; i < n - 1; ++i) {
    for (let j = i + 1; j < n; ++j) {
      correctPairs += arr[i] < arr[j] ? 1 : 0;
    }
  }
  return correctPairs / ((n * (n - 1)) / 2);
}

export function kendallsTauAccuracy(arr) {
  let acc = [];
  for (let i = 0; i < arr.length; ++i) {
    let sum = 0;
    for (let j = 0; j < arr.length; ++j) {
      if (i < j && arr[i] < arr[j]) {
        sum += 1;
      } else if (i > j && arr[i] > arr[j]) {
        sum += 1;
      }
    }
    acc.push(sum / (arr.length - 1));
  }
  return acc;
}

export function rgInterpolate(percent) {
  const p = Math.min(1, Math.max(0, Number(percent) || 0));

  // Higher = pull yellow/red forward.
  // 3 = noticeable
  // 4 = strong but reasonable
  // 6 = very strong
  const contrast = 4;

  // Continuous remap: keeps 0 -> 0 and 1 -> 1,
  // but makes 0.90 / 0.95 visually much less "perfect green".
  const visual = p / (p + contrast * (1 - p));

  const r = Math.round(255 * (visual <= 0.5 ? 1 : 1 - (visual - 0.5) * 2));

  const g = Math.round(255 * (visual >= 0.5 ? 1 : visual * 2));

  return `#${r.toString(16).padStart(2, '0')}${g
    .toString(16)
    .padStart(2, '0')}00`;
}

export function findElementsByKey(arr, key) {
  let results = [];

  arr?.forEach((item) => {
    if (item) {
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        results.push(item[key]);
      }
      Object.values(item).forEach((value) => {
        if (Array.isArray(value)) {
          const nestedResults = findElementsByKey(value, key);
          results = results.concat(nestedResults);
        }
      });
    }
  });

  return results;
}

export function findOnjectByKey(arr, key) {
  let results = [];

  arr?.forEach((item) => {
    if (item) {
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        results.push(item);
      }
      Object.values(item).forEach((value) => {
        if (Array.isArray(value)) {
          const nestedResults = findOnjectByKey(value, key);
          results = results.concat(nestedResults);
        }
      });
    }
  });

  return results;
}

export function convertToTime(number, h, m) {
  const hours = Math.floor(number / 3600);
  const minutes = Math.round((number / 3600 - hours) * 60);
  if (hours === 0 && minutes === 0) return null;
  if (hours > 0) {
    return hours + h + ' ' + minutes + m;
  } else {
    return minutes + m;
  }
}
export function convertToTimeSec(number, h, m) {
  const hours = Math.floor(number / 3600);
  const minutes = Math.floor((number / 3600 - hours) * 60);
  const sec = Math.floor(number - (hours * 3600 + minutes * 60));
  if (hours === 0 && minutes === 0 && sec === 0) return null;
  if (hours > 0) {
    return hours + h + ' ' + minutes + m + ' ' + sec + 's';
  } else {
    if (minutes > 0) {
      return minutes + m + ' ' + sec + 's';
    } else {
      return sec + 's';
    }
  }
}

export function relativePos(child, parent) {
  const parentRect = parent.getBoundingClientRect();
  const childRect = child.getBoundingClientRect();
  return {
    top: childRect.top - parentRect.top,
    left: childRect.left - parentRect.left,
    width: childRect.width,
    height: childRect.height,
  };
}

export function v2equal(a, b) {
  return a[0] == b[0] && a[1] == b[1];
}

// Compute the edit distance between the two given strings
export function stringDistance(a, b) {
  a = a.toLowerCase();
  b = b.toLowerCase();

  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  var matrix = [];

  // increment along the first column of each row
  var i;
  for (i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  // increment each column in the first row
  var j;
  for (j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (i = 1; i <= b.length; i++) {
    for (j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          Math.min(
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1
          )
        ); // deletion
      }
    }
  }

  return matrix[b.length][a.length];
}

export function stringRelativeDistance(a, b) {
  return stringDistance(a, b) / b.length;
}

export function copyToClipboard(str) {
  const el = document.createElement('textarea');
  el.value = str;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export function uploadFile(multiple = false, accept) {
  return new Promise((resolve, reject) => {
    let input = document.createElement('input');
    input.type = 'file';
    input.visbility = 'hidden';
    if (multiple) {
      input.multiple = true;
    }
    if (accept) {
      input.accept = accept;
    }
    input.onchange = (e) => {
      let files;
      if (e.target.files) files = e.target.files;
      else files = [e.target.file];
      resolve(files);
      document.body.removeChild(input);
    };
    document.body.appendChild(input);
    input.click();
  });
}

export function realParseFloat(value) {
  if (value == null || value == undefined) return '';
  return value
    .toString()
    .replace(/[^\d\-.,]/g, '') // Basic sanitization. Allows '-' for negative numbers.
    .replace(',', '.') // Change all commas to periods.
    .replace(/\.(?=.*\.)/g, ''); // Remove all periods except the last one.
}

//extension of str.lastIndexOf
export function nthLastIndexOf(str, searchString, n) {
  if (str === null) return -1;
  if (!n || isNaN(n) || n <= 1) return str.lastIndexOf(searchString);
  n--;
  return str.lastIndexOf(
    searchString,
    nthLastIndexOf(str, searchString, n) - 1
  );
}

//extension of str.indexOf
export function nthIndexOf(str, pattern, n) {
  let index = 0;
  for (let i = 0; i < n; i += 1) {
    if (index !== -1) index = str.indexOf(pattern, index + 1);
  }
  return index;
}

export function getCharForNumber(n) {
  return String.fromCharCode(65 + n);
}

export function useTraceUpdate(props) {
  const prev = useRef(props);
  useEffect(() => {
    const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
      if (prev.current[k] !== v) {
        ps[k] = [prev.current[k], v];
      }
      return ps;
    }, {});
    if (Object.keys(changedProps).length > 0) {
      console.warn('Changed props:', changedProps);
    }
    prev.current = props;
  });
}

export const calculateUnit = (raw, unit = 'volt', maxVolt = 24) => {
  let value;
  let volt;
  let amp;
  switch (unit) {
    case 'volt':
      value = raw;
      return value.toFixed(2) + 'V';

    case 'ampere':
      volt = raw * maxVolt;

      //Todo: choose correct Ohm
      // volt devided with ohm equals ampere
      value = volt / 500;
      return value.toFixed(2) + 'A';

    case 'watt':
      volt = raw * maxVolt;

      //Todo: choose correct Ohm
      // volt devided with ohm equals ampere
      amp = volt / 500;

      // volt multiplied with ampere equals watts
      value = volt * amp;
      return value.toFixed(2) + ' Watt';

    case 'degrees':
      volt = (raw * maxVolt) / 800;

      value = volt * 10;
      return value.toFixed(2) + ' °C';

    case 'farhenheit':
      break;
    case 'bar':
      value = volt * 10;
      return value.toFixed(2) + ' Bar';
  }
  return value;
};

export async function sleep(ms) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

export function array_difference(a, b) {
  let temp = new Set(b);
  return [...a].filter((x) => !temp.has(x));
}

export function array_intersect(a, b) {
  return a.filter(Set.prototype.has, new Set(b));
}

export function swap(arr, i, j) {
  var temp = arr[i];
  arr[i] = arr[j];
  arr[j] = temp;
}

export function useStateAndRef(initial) {
  const [value, setValue] = useState(initial);
  const valueRef = useRef(value);
  valueRef.current = value;
  return [value, setValue, valueRef];
}

export function useStateCallback(initialState) {
  const [state, setState] = useState(initialState);
  const cbRef = useRef(null); // init mutable ref container for callbacks

  const setStateCallback = useCallback((state, cb) => {
    cbRef.current = cb; // store current, passed callback in ref
    setState(state);
  }, []); // keep object reference stable, exactly like `useState`

  useEffect(() => {
    // cb.current is `null` on initial render,
    // so we only invoke callback on state *updates*
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = null; // reset callback after execution
    }
  }, [state]);

  return [state, setStateCallback];
}

//takes a filter str from a earch bar and translates it into a url that can be passed
//to a list endpoint
export function parseFilter(filter) {
  if (!filter) return undefined;
  const trimmed = filter.trim();
  if (!trimmed) return undefined;
  return encodeURIComponent(trimmed);
}

export function parseSelectedTags(filter) {
  if (!filter) return undefined;
  const selectedTags = filter?.map((el) => el.value);
  const strWithSelectedTags = selectedTags.toString();
  return 'tag:' + strWithSelectedTags;
}

export function parseTypeCourse(typeCourse) {
  switch (typeCourse) {
    case 'courses':
      return 'course';
    case 'assessments':
      return 'assessment';
    case 'vacancies':
      return 'leervacature';
    case 'instructions':
      return 'instruction';
    default:
      return 'course';
  }
}

export function parseCourseType(courseType) {
  switch (courseType) {
    case 'course':
      return 'courses';
    case 'assessment':
      return 'assessments';
    case 'leervacature':
      return 'vacancies';
    case 'instruction':
      return 'instructions';
    default:
      return 'courses';
  }
}

//examples:
//  toPercent(2, 3) => 66
//  toPercent(2, 3, 2) => 66.66
//  toPercent(0.5) => 50
//  toPercent(0.3333333) => 33
//  toPercent(0.3333333, null, 2) => 33.33
export function toPercent(score, maxScore = 0, significantDigits = 0) {
  if (!score && !maxScore) return 100;
  if (!score) return '0';
  if (!maxScore) return round(score || 0, significantDigits);
  return parseFloat(
    round(((score || 0) / (maxScore || 1)) * 100, significantDigits)
  );
}

export function parseContentRange(axiosResult) {
  const contentRange = axiosResult?.headers?.['content-range']
    ?.split(' ')?.[1]
    ?.split('/');
  const maxCount = contentRange ? Math.ceil(contentRange?.[1]) : 0;
  return maxCount;
}

export function downloadLink(href, filename) {
  var link = document.createElement('a');
  link.href = href;
  const downloadName = filename || href.split('/').pop();
  link.download = sanitizeFilename(downloadName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/*
export function downloadPDFLink(href, filename) {
  const link = document.createElement('a');
  link.href = href;
  const downloadName = filename || href.split('/').pop();
  link.download = sanitizeFilename(downloadName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
*/

export async function downloadPDFLink(href, filename) {
  // Decide which axios instance to use based on hostname
  const target = new URL(href);
  const isSameHost = target.hostname === new URL(config.hostname).hostname;

  const client = isSameHost ? axios : publicAxios;

  const res = await client.get(href, { responseType: 'blob' });
  if (!res || res.status !== 200) {
    throw new Error(`Download failed: ${res?.status}`);
  }

  const blob = res.data;

  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = sanitizeFilename(filename || href.split('/').pop());
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  a.remove();
}

export const clearCache = (reloadAfterClear = true) => {
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach(async (name) => {
        await caches.delete(name);
      });
    });

    if (reloadAfterClear) window.location.reload();
  }
};

export const useAsyncState = (initialState) => {
  const [state, setState] = useState(initialState);

  const asyncSetState = (value) => {
    return new Promise((resolve) => {
      setState(value);
      setState((current) => {
        resolve(current);
        return current;
      });
    });
  };

  return [state, asyncSetState];
};

export const scrollIntoViewWithOffset = (selector, offset) => {
  window.scrollTo({
    behavior: 'smooth',
    top:
      document.querySelector(selector).getBoundingClientRect().top -
      document.body.getBoundingClientRect().top -
      offset,
  });
};

// fancyWrapper.js — harmless + idempotent + Fancybox v4.0.23
const BOUND_FLAG = '__fancybox_bound_v4__';

function normalizeRoot(node) {
  try {
    if (!node) return null;

    // jQuery object
    if (node.jquery) return node[0] || null;

    // selector string
    if (typeof node === 'string') return document.querySelector(node);

    // document -> use documentElement
    if (node === document) return document.documentElement;

    // element
    if (node.nodeType === 1) return node;

    return null;
  } catch {
    return null;
  }
}

export const fancyWrapper = (node) => {
  try {
    if (!window?.$) return;

    const root = normalizeRoot(node);
    if (!root) return;

    const $root = window.$(root);

    // Wrap images safely (idempotent)
    $root.find('img').each(function () {
      try {
        const $img = window.$(this);
        if ($img.hasClass('nowrap')) return;

        const src = $img.attr('src');
        if (!src) return;

        const $p = $img.parent();

        // already fancybox link -> just ensure href is correct
        if ($p.is('a') && $p.attr('data-fancybox') != null) {
          if ($p.attr('href') !== src) $p.attr('href', src);
          return;
        }

        // don't hijack normal links
        if ($p.is('a')) return;

        // wrap once
        $img.wrap(
          `<a class="fancybox" data-fancybox="gallery" href="${src}"></a>`
        );
      } catch {
        // swallow per-image errors
      }
    });

    // Bind once per root (delegation handles new nodes later)
    if (window.Fancybox?.bind && !root[BOUND_FLAG]) {
      root[BOUND_FLAG] = true;
      window.Fancybox.bind(root, '[data-fancybox]', {
        hideOnContentClick: true,
        hideScrollbar: false,
      });
    }
  } catch {
    // swallow all errors (harmless contract)
  }
};

export function simplifyPath(str) {
  if (!str) return '';
  return str
    .replace(/ +(?= )/g, '')
    .replace(/%/g, '')
    .replace(/\?/g, '')
    .replace(/\|/g, '')
    .replace(/>/g, '')
    .replace(/</g, '')
    .replace(/\*/g, '')
    .replace(/:/g, '')
    .replace(/"/g, '')
    .replace(/\//g, '')
    .replace(/ë/g, '')
    .replace(/\\/g, '')
    .replace(/&/g, '')
    .replace(/\s/g, '_')
    .trim();
}

export function hasParentWithClass(element, className) {
  let parent = element.parentElement;
  while (parent) {
    if (parent.classList.contains(className)) {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
}

export function isEqual(obj1, obj2) {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (
      typeof obj1[key] === 'object' &&
      obj1[key] !== null &&
      typeof obj2[key] === 'object' &&
      obj2[key] !== null
    ) {
      if (!isEqual(obj1[key], obj2[key])) {
        return false;
      }
    } else if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
}

const SINGLE_NBSP_BETWEEN_WORDS =
  /([^\s;>\u00a0])(?:&nbsp;|\u00a0)(?=[^\s&<\u00a0])/g;

export function normalizeNbspHtml(html) {
  if (typeof html !== 'string' || !/&nbsp;|\u00a0/.test(html)) return html;
  return html.replace(SINGLE_NBSP_BETWEEN_WORDS, '$1 ');
}

export function normalizeNbspDeep(value) {
  if (typeof value === 'string') return normalizeNbspHtml(value);
  if (Array.isArray(value)) return value.map(normalizeNbspDeep);
  if (value && typeof value === 'object') {
    const result = {};
    for (const key of Object.keys(value)) {
      result[key] = normalizeNbspDeep(value[key]);
    }
    return result;
  }
  return value;
}

export function orderWidgets(root = document) {
  const labels = root?.querySelectorAll?.('.number_widget');

  let index = 1;
  if (labels?.length > 0) {
    labels.forEach((label) => {
      const widget =
        label.closest('.widget_practical, .widget-placeholder') ||
        label.parentElement;
      const ds = widget
        ?.querySelector?.('[data-state]')
        ?.getAttribute?.('data-state');
      let st = null;
      if (ds) {
        try {
          st = JSON.parse(ds);
        } catch {
          try {
            st = JSON.parse(decode(ds));
          } catch {
            // ignore
          }
        }
      }
      if (!st?.isGroup) {
        label.innerHTML = index;
        ++index;
      }
    });
  }
}

export function focusWidget(uid) {
  const node = document.querySelector(`#${CSS.escape(uid)}`);

  const currentStyle = node?.getAttribute('style');

  if (currentStyle && currentStyle.includes('border')) {
    node.style.border = '1px solid #f8f8f9';
  }
}

export function getInitialsFromName(name) {
  return name
    .split(' ')
    .map((word) => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function mongoEncodeKey(key) {
  return key
    .replace(/\./g, '\uff0e')
    .replace(/\$/g, '\uff04')
    .replace(/\\/g, '\uff3c');
}

export function mongoDecodeKey(key) {
  return key
    .replace('\\u002e', '.')
    .replace('\\u0024', '$')
    .replace('\\\\', '\\');
}

export function convertToPlain(html, limit, elipsis = '...') {
  let tempDivElement = document.createElement('div');
  tempDivElement.innerHTML = html;
  const temp = tempDivElement.textContent || tempDivElement.innerText || '';
  const truncated = temp.slice(0, limit) + '...';
  if (truncated.length < temp.length) return truncated;
  else return temp;
}

export function parseDate(date) {
  if (!date) return '';
  date = new Date(date);
  if (isNaN(date.getTime())) return '';
  var dd = date.getDate(); // Compute the edit distance between the two given strings
  var mm = date.getMonth() + 1;
  var yyyy = date.getFullYear();
  if (dd < 10) {
    dd = '0' + dd;
  }
  if (mm < 10) {
    mm = '0' + mm;
  }
  let result = dd + '/' + mm + '/' + yyyy;
  return result;
}

export function parseDateTime(date) {
  if (!date) return '';
  date = new Date(date);
  if (isNaN(date.getTime())) return '';
  var dd = date.getDate();
  var mm = date.getMonth() + 1;
  var yyyy = date.getFullYear();
  var h = date.getHours();
  var m = date.getMinutes();
  if (dd < 10) {
    dd = '0' + dd;
  }
  if (mm < 10) {
    mm = '0' + mm;
  }
  if (h < 10) {
    h = '0' + h;
  }
  if (m < 10) {
    m = '0' + m;
  }
  return dd + '/' + mm + '/' + yyyy + ' - ' + h + ':' + m;
}

export function walkExtra(root, fn, i = 0, parent = {}) {
  if (!root) return;
  fn(root, i, parent);
  if (root.children) {
    let j = 0;
    for (let child of root.children) {
      walkExtra(child, fn, j, root);
      j++;
    }
  }
  if (root.extraInfo) {
    let j = 0;
    for (let child of root.extraInfo) {
      walkExtra(child, fn, j, root);
      j++;
    }
  }
}

export function bfsWalkExtra(root, fn) {
  const queue = [root];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === null) continue;
    fn(current);
    if (current.children) {
      for (const child of current.children) {
        queue.push(child);
      }
    }
    if (current.extraInfo) {
      for (const child of current.extraInfo) {
        queue.push(child);
      }
    }
  }
}

export function retryWithExponentialBackoff(
  fn,
  maxAttempts = 5,
  baseDelayMs = 100,
  exponent = 1.5
) {
  let attempt = 1;

  const execute = async () => {
    try {
      return await fn();
    } catch (error) {
      if (attempt >= maxAttempts) {
        throw error;
      }
      const delayMs = baseDelayMs * Math.pow(exponent, attempt);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      attempt++;
      return execute();
    }
  };
  return execute();
}

export function extractTextFromElement(element) {
  let result = '';
  element?.childNodes?.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      result += child.textContent;
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const tagName = child.tagName.toLowerCase();
      if (['p', 'div', 'li'].includes(tagName)) {
        result += '\n';
      }
      if (tagName === 'li') {
        result += '- ';
      }
      if (tagName === 'a') {
        result += child.textContent + ` (${child.getAttribute('href')})`;
      } else if (tagName === 'span') {
        result += ' ' + extractTextFromElement(child) + ' ';
      } else {
        result += extractTextFromElement(child);
      }
      if (['p', 'div', 'li', 'ul', 'ol'].includes(tagName)) {
        result += '\n';
      }
    }
  });
  return result;
}

export function htmlDecode(input) {
  var doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent;
}

export function secondsToIsoDuration(seconds) {
  const hours = Math.floor(seconds / 3600); // Calculate hours
  seconds %= 3600; // Remainder after hours
  const minutes = Math.floor(seconds / 60); // Calculate minutes
  seconds %= 60; // Remaining seconds

  // Build the duration string in ISO 8601 format
  let duration = 'PT';
  if (hours > 0) duration += `${hours}H`;
  if (minutes > 0) duration += `${minutes}M`;
  if (seconds > 0 || duration === 'PT') duration += `${seconds}S`;

  return duration;
}

export function parseNumber(n, fallback) {
  let number = parseFloat(n);
  if (!number && number !== 0) return fallback;
  return number;
}

export function getLocalizedText(field, lang) {
  if (typeof field === 'string') return field;
  if (field?.['#text']) {
    const fieldLang = field?.['@_lang']?.toLowerCase();
    if (
      lang.toLowerCase().startsWith(fieldLang) ||
      fieldLang.startsWith(lang.toLowerCase())
    ) {
      return field['#text'];
    }
  }
  return field?.['#text'];
}

let cachedCameraAccess = null;
export async function canAttemptCameraAccess() {
  if (cachedCameraAccess !== null) {
    return cachedCameraAccess;
  }

  const isSecureContext = window.isSecureContext;
  const hasMediaDevices = !!(
    navigator.mediaDevices && navigator.mediaDevices.getUserMedia
  );

  if (!isSecureContext || !hasMediaDevices) {
    cachedCameraAccess = false;
    return cachedCameraAccess;
  }

  if (navigator.permissions?.query) {
    try {
      const result = await navigator.permissions.query({ name: 'camera' });
      cachedCameraAccess = result.state !== 'denied';
      return cachedCameraAccess;
    } catch {
      cachedCameraAccess = true; // Fail open
      return cachedCameraAccess;
    }
  }

  cachedCameraAccess = true;
  return cachedCameraAccess;
}

export function sanitizeFilename(input) {
  return input
    .normalize('NFKD') // Normalize accents/diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .replace(/[^a-zA-Z0-9-_.]/g, '_') // Replace unsupported chars
    .replace(/_+/g, '_') // Collapse multiple underscores
    .replace(/^_+|_+$/g, '') // Trim leading/trailing underscores
    .substring(0, 255); // Limit to 255 characters
}

export function safeFilename(filename) {
  if (!filename) return '';
  return encodeURI(sanitizeFilename(filename));
}

export function superSafeFilename(filename) {
  // Keep only safe characters: a-z, A-Z, 0-9, underscore, hyphen, dot
  return filename.replace(/[^a-zA-Z0-9_.-]/g, '');
}

export function renderTemplate(template, data) {
  return template.replace(/{{\s*(\w+)\s*}}/g, (_, key) => {
    return data[key] !== undefined ? data[key] : '';
  });
}

export function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export const pastels = [
  '#C25FF4',
  '#E3BF00',
  '#6EC6C0',
  '#F25850',
  '#29BAFF',
  '#AE7226',
  '#1173DD',
  '#9AD055',
  '#807EFF',
  '#FF8EA4',
];

export const fmt = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Compact time like: 74–81s, 1:14–1:21, or 1:01:14–1:02:21
export function fmtRangeCompact(startMs = 0, endMs = 0) {
  const s1 = Math.max(0, Math.floor(startMs / 1000));
  const s2 = Math.max(0, Math.floor(endMs / 1000));

  const fmt = (s) => {
    if (s < 600) return `${s}s`; // under 10 min -> seconds
    const m = Math.floor(s / 60),
      sec = s % 60;
    if (s < 3600) return `${m}:${String(sec).padStart(2, '0')}`; // under 1h -> M:SS
    const h = Math.floor(m / 60),
      mm = m % 60;
    return `${h}:${String(mm).padStart(2, '0')}:${String(sec).padStart(
      2,
      '0'
    )}`; // H:MM:SS
  };
  return `${fmt(s1)}–${fmt(s2)}`;
}

export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const abbr = (str = '', n = 22) =>
  str.length > n ? str.slice(0, n - 1) + '…' : str;

export function capitalizeFirst(str) {
  if (typeof str !== 'string' || str.length === 0) return str;

  const locale = i18n.resolvedLanguage || i18n.language;

  // Capitalize first Unicode letter (leaves leading spaces/punctuation as-is)
  return str.replace(/\p{L}/u, (ch) =>
    locale ? ch.toLocaleUpperCase(locale) : ch.toLocaleUpperCase()
  );
}

export function decodeHtml(value = '') {
  const txt = document.createElement('textarea');
  txt.innerHTML = value;
  return txt.value;
}

function hashStringToSeed(str) {
  let hash = 2166136261;

  for (let i = 0; i < String(str).length; i++) {
    hash ^= String(str).charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function seededRandom(seed) {
  let value = seed;

  return function () {
    value += 0x6d2b79f5;

    let t = value;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffleBySeed(arr, seedValue) {
  const random = seededRandom(hashStringToSeed(seedValue));

  return arr
    .map((item) => [item, random()])
    .sort((a, b) => a[1] - b[1])
    .map(([item]) => item);
}

export async function getTagsFromImageUrl(imageUrl, existingTags, authId) {
  if (!imageUrl) return [];

  let lang;

  if (i18n.language === 'en') {
    lang = 'English';
  } else if (i18n.language === 'fr') {
    lang = 'French';
  } else if (i18n.language === 'es') {
    lang = 'Spanish';
  } else if (i18n.language === 'de') {
    lang = 'German';
  } else {
    lang = 'Dutch';
  }

  const prompt = `
  Analyze the image and generate relevant search tags.

  Rules:
  - Return exactly 10 tags if possible.
  - Tags must be in ${lang}.
  - Tags must be short, concise, and useful for image search.
  - Do not duplicate existing tags - Existing tags:
  ${JSON.stringify(existingTags)}.
  - Do not return near-duplicate synonyms of existing tags.
  - Prefer technical, educational, and professional terminology.
  - Prioritize tags related to:
    - engineering
    - electrical engineering
    - electronics
    - mechanics
    - mechanical engineering
    - physics
    - mathematics
    - automation
    - manufacturing
    - industrial processes
    - innovation
    - technology
    - STEM education
    - scientific concepts
  - If the image contains people, tools, machines, diagrams, measurements, equipment, or technical processes, generate tags that describe the technical context rather than generic visual descriptions.
  - Avoid generic tags such as: image, photo, picture, person, man, woman, object, background.
  - Do not include hashtags.
  - Do not include explanations.
  - Return only a valid JSON object in the format:

  {
    "tags": ["tag1", "tag2", "tag3"]
  }
  `;

  try {
    const dataOpenAI = await axios.post(`${config.backend}/openai`, {
      response_format: { type: 'json_object' },
      messages: [
        {
          role: 'developer',
          content:
            'You analyze images and return only valid JSON. The response must contain a key "tags" with an array of strings.',
        },
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: prompt,
            },
            {
              type: 'image_url',
              image_url: {
                url: imageUrl,
              },
            },
          ],
        },
      ],
      model: 'gpt-5.4-mini',
      temperature: 0.2,
      max_completion_tokens: 500,
      user: `${authId}`,
    });

    const fullAnswer = dataOpenAI?.data?.choices?.[0]?.message?.content || '';

    const jsonObject = JSON.parse(fullAnswer);

    const tags = Array.isArray(jsonObject)
      ? jsonObject
      : Array.isArray(jsonObject?.tags)
        ? jsonObject.tags
        : [];

    const existingLower = new Set(
      existingTags.map((tag) => String(tag).trim().toLowerCase())
    );

    return tags
      .map((tag) => String(tag).trim())
      .filter(Boolean)
      .filter((tag) => !existingLower.has(tag.toLowerCase()))
      .slice(0, 10);
  } catch (error) {
    console.error('Error getting image tags:', error);
    return [];
  }
}
